import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Mail, Eye, ChevronDown, Monitor, Smartphone, Tablet, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ORDER = ['Reminders', 'Notifications', 'Reports', 'System'];
const DEVICE_PRESETS = [
  { id: 'desktop', label: 'Desktop', icon: Monitor, width: '100%' },
  { id: 'tablet', label: 'Tablet', icon: Tablet, width: '768px' },
  { id: 'mobile', label: 'Mobile', icon: Smartphone, width: '375px' },
];

export default function EmailTemplatePreview() {
  const [templates, setTemplates] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [loading, setLoading] = useState(false);
  const [device, setDevice] = useState('desktop');
  const [showList, setShowList] = useState(false);
  const iframeRef = useRef(null);

  useEffect(() => { loadTemplates(); }, []);

  const loadTemplates = async () => {
    try {
      const res = await axios.get(`${API}/email-templates/types`);
      setTemplates(res.data);
      if (res.data.length > 0) loadPreview(res.data[0].id);
    } catch { toast.error('Failed to load email templates'); }
  };

  const loadPreview = async (templateType) => {
    setSelectedType(templateType);
    setShowList(false);
    setLoading(true);
    try {
      const res = await axios.get(`${API}/email-templates/preview/${templateType}`);
      setPreviewHtml(res.data.html);
    } catch { toast.error('Failed to load preview'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (iframeRef.current && previewHtml) {
      const iframe = iframeRef.current;
      const timer = setTimeout(() => {
        const doc = iframe.contentDocument;
        if (doc) { doc.open(); doc.write(previewHtml); doc.close(); }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [previewHtml]);

  const grouped = CATEGORY_ORDER.reduce((acc, cat) => {
    const items = templates.filter(t => t.category === cat);
    if (items.length) acc[cat] = items;
    return acc;
  }, {});

  const selectedTemplate = templates.find(t => t.id === selectedType);
  const activeDevice = DEVICE_PRESETS.find(d => d.id === device);

  return (
    <DashboardLayout>
      <div className="h-full flex flex-col" data-testid="email-template-preview-page">

        {/* ── Header ── */}
        <div className="flex-shrink-0 border-b border-slate-200 bg-white px-4 md:px-6 py-3 md:py-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-8 h-8 md:w-9 md:h-9 rounded-lg bg-[#37519b]/10 flex items-center justify-center flex-shrink-0">
                <Mail className="w-4 h-4 md:w-5 md:h-5 text-[#37519b]" />
              </div>
              <div className="min-w-0">
                <h1 className="text-sm md:text-lg font-semibold text-slate-900 truncate">Email Templates</h1>
                <p className="text-xs text-slate-500 hidden md:block">Preview outgoing emails with current branding</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Device toggles — hidden on mobile */}
              <div className="hidden md:flex items-center bg-slate-100 rounded-lg p-0.5" data-testid="device-toggle">
                {DEVICE_PRESETS.map(d => {
                  const Icon = d.icon;
                  return (
                    <button
                      key={d.id}
                      onClick={() => setDevice(d.id)}
                      data-testid={`device-${d.id}`}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                        device === d.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      {d.label}
                    </button>
                  );
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => selectedType && loadPreview(selectedType)}
                data-testid="refresh-preview-btn"
                className="h-8"
              >
                <RefreshCw className="w-3.5 h-3.5 md:mr-1.5" />
                <span className="hidden md:inline">Refresh</span>
              </Button>
            </div>
          </div>
        </div>

        {/* ── Mobile Template Selector ── */}
        <div className="md:hidden flex-shrink-0 border-b border-slate-200 bg-white px-4 py-2">
          <button
            onClick={() => setShowList(!showList)}
            data-testid="mobile-template-selector"
            className="w-full flex items-center justify-between px-3 py-2.5 bg-slate-50 rounded-lg text-sm"
          >
            <span className="font-medium text-slate-800 truncate">
              {selectedTemplate?.name || 'Select template...'}
            </span>
            <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 ml-2 transition-transform ${showList ? 'rotate-180' : ''}`} />
          </button>

          {showList && (
            <div className="mt-2 max-h-64 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg">
              {Object.entries(grouped).map(([category, items]) => (
                <div key={category}>
                  <div className="px-3 py-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider bg-slate-50 sticky top-0">
                    {category}
                  </div>
                  {items.map(t => (
                    <button
                      key={t.id}
                      onClick={() => loadPreview(t.id)}
                      data-testid={`mobile-template-${t.id}`}
                      className={`w-full text-left px-3 py-2.5 text-sm border-b border-slate-100 last:border-0 ${
                        selectedType === t.id ? 'bg-[#37519b]/5 text-[#37519b] font-medium' : 'text-slate-700'
                      }`}
                    >
                      {t.name}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Body ── */}
        <div className="flex-1 flex overflow-hidden">
          {/* Desktop Sidebar — hidden on mobile */}
          <div className="hidden md:block w-72 flex-shrink-0 border-r border-slate-200 bg-slate-50/50 overflow-y-auto" data-testid="template-sidebar">
            <div className="p-4 space-y-5">
              {Object.entries(grouped).map(([category, items]) => (
                <div key={category}>
                  <h3 className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2 px-2">
                    {category}
                  </h3>
                  <div className="space-y-0.5">
                    {items.map(t => (
                      <button
                        key={t.id}
                        onClick={() => loadPreview(t.id)}
                        data-testid={`template-${t.id}`}
                        className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-all flex items-center justify-between group ${
                          selectedType === t.id
                            ? 'bg-[#37519b] text-white shadow-sm'
                            : 'text-slate-700 hover:bg-white hover:shadow-sm'
                        }`}
                      >
                        <div className="min-w-0">
                          <div className={`font-medium truncate text-[13px] ${selectedType === t.id ? 'text-white' : ''}`}>
                            {t.name}
                          </div>
                          <div className={`text-[11px] mt-0.5 truncate ${
                            selectedType === t.id ? 'text-white/70' : 'text-slate-400'
                          }`}>
                            {t.description}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Preview area */}
          <div className="flex-1 bg-slate-100 overflow-auto flex flex-col items-center p-3 md:p-6" data-testid="preview-area">
            {selectedTemplate && (
              <div className="mb-3 md:mb-4 flex items-center gap-2 text-xs md:text-sm text-slate-500">
                <Eye className="w-3.5 h-3.5 md:w-4 md:h-4" />
                <span>Previewing: <strong className="text-slate-700">{selectedTemplate.name}</strong></span>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center h-64">
                <RefreshCw className="w-5 h-5 text-slate-400 animate-spin" />
              </div>
            ) : (
              <div
                className="bg-white rounded-lg md:rounded-xl shadow-lg overflow-hidden transition-all duration-300 w-full md:w-auto"
                style={{
                  width: typeof window !== 'undefined' && window.innerWidth < 768 ? '100%' : (activeDevice?.width || '100%'),
                  maxWidth: '100%',
                }}
              >
                <iframe
                  ref={iframeRef}
                  title="Email Preview"
                  data-testid="email-preview-iframe"
                  className="w-full border-0"
                  style={{ minHeight: '600px', height: '100%' }}
                  sandbox="allow-same-origin"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
